import React from 'react'

// Components
import {
  Button,
  CopyToClipboard,
  ErrorMessage,
  FlatButton,
  LoadingPlaceholder,
  ModalInjectedProps,
  Tab,
  TabPanel,
  Tabs,
  useForTablet,
  useModalContext,
} from '@sketch/components'
import LeaveShareModal from 'modules/shares/components/LeaveShareModal/LeaveShareModal'
import { SharingModalHeader } from './SharingModalHeader'
import {
  WorkspaceMembersPanel,
  WorkspaceMembersTitle,
} from './WorkspaceMembersPanel'
import { GuestsTitle, GuestsPanel } from './GuestsPanel'
import { EveryoneTitle, EveryonePanel } from './EveryonePanel'

// Styles
import {
  StyledModal as Modal,
  StyledTabList as TabList,
  TabPanelWrapper,
} from './SharingModal.styles'

// GraphQL
import {
  useGetShareMembershipsQuery,
  useGetShareQuery,
  ShareMembershipInfoFragment,
} from '@sketch/gql-types'

interface SharingModalProps extends ModalInjectedProps {
  identifier: string
  name: string
}

const SharingModal = ({ hideModal, identifier, name }: SharingModalProps) => {
  const { showModal } = useModalContext()

  const handleOnLeaveDocument = () => {
    showModal(LeaveShareModal, {
      shareIdentifier: identifier,
      shareName: name,
    })
  }

  const { data, loading, error } = useGetShareQuery({
    variables: { shortId: identifier },
  })

  const isTabletAndBigger = useForTablet()
  const isMobile = !isTabletAndBigger

  // we need this query for the guests count
  // @TODO: with Cloud#17164, this should be a new query just to get the meta instead of the whole list
  const { data: membershipsData } = useGetShareMembershipsQuery({
    fetchPolicy: 'cache-and-network',
    variables: {
      shareIdentifier: identifier,
      // @TODO: this filter will be possible after Cloud#17164
      //   filter: {type: guestsOnly ? 'GUEST' : undefined},
    },
  })

  const memberships = (membershipsData?.share?.memberships?.entries ??
    []) as ShareMembershipInfoFragment[]

  const guestsCount = memberships.filter(
    // @TODO: this manual filter will be removed after Cloud#17164
    (member: any) => member.workspaceMembership.role === 'GUEST'
  ).length

  if (loading) {
    return (
      <Modal onCancel={hideModal}>
        <SharingModalHeader />
        <Modal.Body>
          <LoadingPlaceholder size="64px" />
        </Modal.Body>
      </Modal>
    )
  }

  const share = data?.share

  if (error || !share) {
    return (
      <Modal onCancel={hideModal}>
        <SharingModalHeader />
        <Modal.Body>
          <ErrorMessage.Generic />
        </Modal.Body>
      </Modal>
    )
  }

  const hideLeaveButton = !share.userCanLeaveShare

  return (
    <Modal onCancel={hideModal}>
      <SharingModalHeader share={share} />
      <Modal.Body>
        <Tabs>
          <TabList shouldDrawSeparator>
            <Tab>
              <WorkspaceMembersTitle share={share} />
            </Tab>
            <Tab>
              <GuestsTitle guestsCount={guestsCount} />
            </Tab>
            <Tab>
              <EveryoneTitle isPublic={share.publicAccessLevel !== 'NONE'} />
            </Tab>
          </TabList>
          <TabPanel>
            <TabPanelWrapper>
              <WorkspaceMembersPanel share={share} />
            </TabPanelWrapper>
          </TabPanel>
          <TabPanel>
            <TabPanelWrapper>
              <GuestsPanel share={share} />
            </TabPanelWrapper>
          </TabPanel>
          <TabPanel>
            <TabPanelWrapper>
              <EveryonePanel share={share} />
            </TabPanelWrapper>
          </TabPanel>
        </Tabs>
      </Modal.Body>
      <Modal.Footer
        leftContent={
          hideLeaveButton ? undefined : (
            <FlatButton variant="negative" onClick={handleOnLeaveDocument}>
              Leave Document&hellip;
            </FlatButton>
          )
        }
      >
        {isMobile && (
          <CopyToClipboard
            size="40"
            showIcon={false}
            value={share.publicUrl}
            title={
              share.publicAccessLevel !== 'NONE'
                ? 'Copy Public Link'
                : 'Copy Link'
            }
          />
        )}
        <Button variant="primary" onClick={hideModal}>
          Done
        </Button>
      </Modal.Footer>
    </Modal>
  )
}

export { SharingModal as default }
